import React, { useState, useEffect, useCallback } from 'react';
import { useBackClose } from './backButton';
import axios from 'axios';
import { useAuth } from './AuthContext';
import EventDetails from './EventDetails';
import './EventFeed.css';
import { translateText, detectLanguage } from './translationUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventFeed = ({ onClose, selectedEventId = null, categoryFilter = null, statusFilter = null, language = 'ru', onShowOnMap }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  // Кнопка «Назад» закрывает детали события (возврат к ленте)
  useBackClose(!!selectedEvent, () => setSelectedEvent(null));
  const [filter, setFilter] = useState('all'); // all, emergency, recent, my_events
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, priority

  // Function to get auto-translated description
  const getTranslatedDescription = (event) => {
    let description = '';
    
    if (language === 'kk') {
      // If interface is Kazakh, prefer Kazakh description or translate Russian
      description = event.description_kk || event.description_ru || '';
      if (!event.description_kk && event.description_ru) {
        description = translateText(event.description_ru, 'ru', 'kk');
      }
    } else {
      // If interface is Russian, prefer Russian description or translate Kazakh  
      description = event.description_ru || event.description_kk || '';
      if (!event.description_ru && event.description_kk) {
        description = translateText(event.description_kk, 'kk', 'ru');
      }
    }
    
    return description;
  };

  const t = {
    kk: {
      title: "Оқиғалар таспасы",
      allEvents: "Барлық оқиғалар",
      emergencyOnly: "Тек ТЖ",
      recentEvents: "Жуықтағы оқиғалар",
      myEvents: "Менің оқиғаларым",
      sortNewest: "Жаңалар",
      sortOldest: "Ескілер",
      sortPriority: "Маңыздылық",
      status: "Мәртебе",
      priority: "Маңыздылық",
      address: "Мекенжай",
      assignedTo: "Тағайындалған",
      createdBy: "Жасаған",
      photos: "фото",
      comments: "пікірлер",
      backToFeed: "Таспаға оралу",
      noEvents: "Оқиғалар жоқ",
      loading: "Жүктелуде...",
      error: "Қате орын алды"
    },
    ru: {
      title: "Лента событий",
      allEvents: "Все события",
      emergencyOnly: "Только ЧС",
      recentEvents: "Недавние события",
      myEvents: "Мои события",
      sortNewest: "Сначала новые",
      sortOldest: "Сначала старые", 
      sortPriority: "По приоритету",
      status: "Статус",
      priority: "Приоритет",
      address: "Адрес",
      assignedTo: "Назначено",
      createdBy: "Создал",
      photos: "фото",
      comments: "комментариев",
      backToFeed: "Назад к ленте",
      noEvents: "Нет событий",
      loading: "Загрузка...",
      error: "Произошла ошибка"
    }
  };

  const currentT = t[language] || t.ru;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    console.log('EventFeed: selectedEventId changed:', selectedEventId); // Debug log
    if (selectedEventId && events.length > 0) {
      const event = events.find(e => e.id === selectedEventId);
      console.log('EventFeed: Found event:', event); // Debug log
      if (event) {
        setSelectedEvent(event);
      }
    }
  }, [selectedEventId, events]);

  // Set filter when categoryFilter is provided
  useEffect(() => {
    if (categoryFilter) {
      setFilter(`category:${categoryFilter}`);
    } else if (statusFilter) {
      setFilter(statusFilter);
    } else {
      setFilter('all');
    }
  }, [categoryFilter, statusFilter]);

  const applyFilters = useCallback(() => {
    let filtered = [...events];
    console.log('EventFeed: Applying filters. Total events:', events.length);
    console.log('EventFeed: Current filter:', filter);

    if (filter.startsWith('category:')) {
      const categoryId = filter.split(':')[1];
      filtered = filtered.filter(event => event.category === categoryId);
      console.log('EventFeed: Category filter applied. Filtered events:', filtered.length);
    } else {
      switch (filter) {
        case 'emergency':
          filtered = filtered.filter(event => event.category === 'emergency');
          break;
        case 'recent':
          const threeDaysAgo = new Date();
          threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
          filtered = filtered.filter(event => new Date(event.created_at) >= threeDaysAgo);
          break;
        case 'pending':
          filtered = filtered.filter(event => event.status === 'pending' || event.status === 'new');
          break;
        case 'in_progress':
          filtered = filtered.filter(event => event.status === 'in_progress' || event.status === 'assigned');
          break;
        case 'completed':
          filtered = filtered.filter(event => event.status === 'completed' || event.status === 'final_completed');
          break;
        case 'my_events':
          if (user) {
            filtered = filtered.filter(event => event.created_by === user.id);
          }
          break;
        case 'assigned_to_me':
          if (user) {
            filtered = filtered.filter(event => event.assigned_to === user.username || event.assigned_to === user.id);
          }
          break;
        case 'high_priority':
          filtered = filtered.filter(event => event.priority >= 4);
          break;
        case 'all':
        default:
          console.log('EventFeed: Showing all events');
          break;
      }
    }

    switch (sortBy) {
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'priority':
        filtered.sort((a, b) => b.priority - a.priority);
        break;
      default:
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
    }

    setFilteredEvents(filtered);
  }, [events, filter, sortBy, user]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [eventsResponse, categoriesResponse] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/categories`)
      ]);
      
      console.log('Loaded events:', eventsResponse.data.length);
      setEvents(eventsResponse.data);
      setCategories(categoriesResponse.data);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(currentT.error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryData = (categoryId) => {
    return categories.find(cat => cat.id === categoryId) || {
      name_ru: categoryId,
      name_kk: categoryId,
      color: '#6b7c6e',
      icon: '📍'
    };
  };

  const getStatusText = (status) => {
    const statusMap = {
      kk: {
        new: "Жаңа",
        assigned: "Тағайындалған",
        in_progress: "Орындалуда",
        completed: "Аяқталған",
        cancelled: "Тоқтатылған"
      },
      ru: {
        new: "Новое",
        assigned: "Назначено",
        in_progress: "В работе",
        completed: "Завершено",
        cancelled: "Отменено"
      }
    };
    return statusMap[language]?.[status] || status;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(language === 'kk' ? 'kk-KZ' : 'ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Маппинг типов служб к названиям (двуязычно)
  const SERVICE_NAMES = {
    emergency: { ru: 'Служба ЧС', kk: 'ТЖ қызметі' },
    municipal: { ru: 'Служба ЖКХ', kk: 'ТКШ қызметі' },
    architecture: { ru: 'Отдел Архитектуры', kk: 'Сәулет бөлімі' },
    heating: { ru: 'Теплоцентраль', kk: 'Жылу орталығы' },
    electricity: { ru: 'ОЭСК', kk: 'ОЭСК' },
    telecom: { ru: 'Казахтелеком', kk: 'Қазақтелеком' },
    traffic_police: { ru: 'ГАИ', kk: 'ЖАИ' },
    police: { ru: 'Полиция', kk: 'Полиция' },
    improvement: { ru: 'Служба благоустройства «Зырян Тәртіп»', kk: 'Абаттандыру қызметі «Зырян Тәртіп»' },
    education: { ru: 'ГОРОНО', kk: 'Білім бөлімі' },
    culture_sport: { ru: 'Отдел Культуры и Спорта', kk: 'Мәдениет және спорт бөлімі' },
    waste: { ru: 'ШемКомХоз', kk: 'ШемКомХоз' },
    healthcare: { ru: 'Горздрав', kk: 'Денсаулық сақтау' },
    lighting: { ru: 'Уличное освещение', kk: 'Көше жарығы' }
  };

  const getServiceName = (serviceType) => {
    return SERVICE_NAMES[serviceType]?.[language] || null;
  };

  const getAssignedServiceName = (event) => {
    const fallback = language === 'kk' ? 'Қызмет' : 'Служба';

    // Если есть assigned_to_user с service_type
    if (event.assigned_to_user?.service_type) {
      return getServiceName(event.assigned_to_user.service_type) || 
             event.assigned_to_user.full_name || 
             fallback;
    }
    
    // Если только assigned_to ID - определяем по подстроке
    if (event.assigned_to) {
      const id = event.assigned_to.toLowerCase();
      const substrings = {
        emergency: 'emergency',
        municipal: 'municipal',
        improvement: 'improvement',
        architecture: 'architecture',
        heating: 'heating',
        electricity: 'electricity',
        telecom: 'telecom',
        traffic: 'traffic_police',
        police: 'police',
        education: 'education',
        culture: 'culture_sport',
        waste: 'waste',
        healthcare: 'healthcare',
        lighting: 'lighting'
      };
      for (const [key, serviceType] of Object.entries(substrings)) {
        if (id.includes(key)) return SERVICE_NAMES[serviceType][language];
      }
    }
    
    return fallback;
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  const handleBackToFeed = () => {
    setSelectedEvent(null);
  };

  if (selectedEvent) {
    return (
      <EventDetails
        event={selectedEvent}
        categories={categories}
        language={language}
        onClose={onClose}
        onBack={handleBackToFeed}
        onShowOnMap={onShowOnMap}
      />
    );
  }

  return (
    <div className="event-feed-overlay">
      <div className="event-feed">
        <div className="event-feed-header">
          <h2>{currentT.title}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="event-feed-controls">
          <div className="filter-controls">
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="filter-select"
              style={{ minWidth: '200px', maxHeight: '120px' }}
            >
              <option value="all">{currentT.allEvents}</option>
              <option value="emergency">{currentT.emergencyOnly}</option>
              <option value="recent">{currentT.recentEvents}</option>
              <option value="pending">{language === 'kk' ? 'Күтудегілер' : 'Ожидающие'}</option>
              <option value="in_progress">{language === 'kk' ? 'Орындалуда' : 'В работе'}</option>
              <option value="completed">{language === 'kk' ? 'Орындалғандар' : 'Выполненные'}</option>
              <option value="high_priority">{language === 'kk' ? 'Жоғары маңыздылық' : 'Высокий приоритет'}</option>
              {user && <option value="my_events">{currentT.myEvents}</option>}
              {user?.role === 'service' && <option value="assigned_to_me">{language === 'kk' ? 'Маған тағайындалған' : 'Назначенные мне'}</option>}
              {user?.role === 'admin' && <option value="emergency">{language === 'kk' ? 'Төтенше' : 'Экстренные'}</option>}
            </select>

            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="newest">{currentT.sortNewest}</option>
              <option value="oldest">{currentT.sortOldest}</option>
              <option value="priority">{currentT.sortPriority}</option>
            </select>
          </div>

          <div className="event-count">
            {filteredEvents.length} {language === 'kk' ? 'оқиға' : 'событий'}
          </div>
        </div>

        <div className="event-feed-content">
          {loading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>{currentT.loading}</p>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadData}>{language === 'kk' ? 'Қайталап көру' : 'Попробовать снова'}</button>
            </div>
          )}

          {!loading && !error && filteredEvents.length === 0 && (
            <div className="empty-state">
              <p>{currentT.noEvents}</p>
            </div>
          )}

          <div className="events-list">
            {filteredEvents.map((event) => {
              const categoryData = getCategoryData(event.category);
              const hasPhotos = event.photos && event.photos.length > 0;
              const hasComments = event.comments && event.comments.length > 0;

              return (
                <div 
                  key={event.id} 
                  className={`event-card ${event.category === 'emergency' ? 'emergency' : ''}`}
                  onClick={() => handleEventClick(event)}
                >
                  <div className="event-card-header">
                    <div className="event-category">
                      <span 
                        className="category-icon"
                        style={{ backgroundColor: categoryData.color }}
                      >
                        {categoryData.icon}
                      </span>
                      <span className="category-name">
                        {language === 'kk' ? categoryData.name_kk : categoryData.name_ru}
                      </span>
                    </div>
                    <div className="event-meta">
                      <span className={`event-status status-${event.status}`}>
                        {getStatusText(event.status)}
                      </span>
                      {event.priority > 3 && (
                        <span className="priority-high">
                          {'⭐'.repeat(event.priority)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="event-card-content">
                    {/* Добавляем отображение фото если есть */}
                    {event.photos && event.photos.length > 0 && (
                      <div className="event-photo-preview">
                        <img 
                          src={event.photos[0]} 
                          alt="Event photo" 
                          className="event-thumbnail"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {event.photos.length > 1 && (
                          <div className="photo-count-badge">
                            +{event.photos.length - 1}
                          </div>
                        )}
                      </div>
                    )}
                    
                    <h3 className="event-description">
                      {getTranslatedDescription(event)}
                    </h3>
                    
                    {event.address && (
                      <p className="event-address">
                        📍 {event.address}
                      </p>
                    )}

                    <div className="event-details">
                      <span className="event-time">
                        🕒 {formatDate(event.created_at)}
                      </span>
                      
                      {hasPhotos && (
                        <span className="event-photos">
                          📷 {event.photos.length} {currentT.photos}
                        </span>
                      )}

                      {hasComments && (
                        <span className="event-comments">
                          💬 {event.comments.length} {currentT.comments}
                        </span>
                      )}
                    </div>

                    {/* Отображение назначенной службы */}
                    {(event.assigned_to_user || event.assigned_to) && (
                      <div className="event-assignment">
                        👨‍🔧 {currentT.assignedTo}: {getAssignedServiceName(event)}
                      </div>
                    )}
                  </div>

                  <div className="event-card-footer">
                    <span className="created-by">
                      {currentT.createdBy}: Житель {event.created_by_user?.full_name ? event.created_by_user.full_name.split(' ')[0] : ''}
                    </span>
                    <span className="click-hint">
                      {language === 'kk' ? 'Толық ақпарат үшін басыңыз →' : 'Нажмите для подробностей →'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventFeed;